import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { format, addDays, addWeeks, startOfDay, isSameDay, parseISO, startOfWeek, endOfWeek, eachDayOfInterval, startOfMonth, endOfMonth, getDay, addMonths, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { FiCalendar, FiClock, FiX, FiChevronLeft, FiChevronRight, FiCornerDownLeft } from 'react-icons/fi';

const DatePickerPopover = ({ date, onDateChange, onClose, position }) => {
  const [selectedDate, setSelectedDate] = useState(date ? (typeof date === 'string' ? parseISO(date) : date) : null);
  const [currentMonth, setCurrentMonth] = useState(selectedDate ? new Date(selectedDate) : new Date());
  const [time, setTime] = useState(date ? format(typeof date === 'string' ? parseISO(date) : date, 'HH:mm') : null);
  const [inputValue, setInputValue] = useState('');
  const popoverRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Update input value when date changes
  useEffect(() => {
    if (selectedDate) {
      let label = format(selectedDate, "d 'de' MMM", { locale: es });
      if (isSameDay(selectedDate, new Date())) label = "Hoy";
      else if (isSameDay(selectedDate, addDays(new Date(), 1))) label = "Mañana";

      if (time) label += `, ${time}`;
      setInputValue(label);
    } else {
      setInputValue('');
    }
  }, [selectedDate, time]);

  // Shortcuts
  const shortcuts = [
    { label: 'Hoy', value: startOfDay(new Date()), dayLabel: format(new Date(), 'EEE', { locale: es }) },
    { label: 'Mañana', value: startOfDay(addDays(new Date(), 1)), dayLabel: format(addDays(new Date(), 1), 'EEE', { locale: es }) },
    { label: 'Este fin de semana', value: startOfDay(addDays(new Date(), (6 - new Date().getDay() + 7) % 7)), dayLabel: 'sáb.' },
    { label: 'Próxima semana', value: startOfDay(addWeeks(new Date(), 1)), dayLabel: format(addWeeks(new Date(), 1), 'EEE', { locale: es }) },
    { label: '2 semanas', value: startOfDay(addWeeks(new Date(), 2)), dayLabel: format(addWeeks(new Date(), 2), 'EEE', { locale: es }) },
    { label: '4 semanas', value: startOfDay(addWeeks(new Date(), 4)), dayLabel: format(addWeeks(new Date(), 4), 'EEE', { locale: es }) },
  ];

  const handleDateSelect = (d) => {
    const newDate = new Date(d);
    if (time) {
      const [hours, minutes] = time.split(':');
      newDate.setHours(parseInt(hours), parseInt(minutes));
    } else {
      newDate.setHours(0, 0, 0, 0);
    }
    setSelectedDate(newDate);
    // Save immediately (date-only, no time required)
    onDateChange(newDate.toISOString());
  };

  const handleTimeSelect = (t) => {
    setTime(t);
    if (selectedDate) {
      const newDate = new Date(selectedDate);
      const [hours, minutes] = t.split(':');
      newDate.setHours(parseInt(hours), parseInt(minutes));
      onDateChange(newDate.toISOString());
      onClose();
    }
  };

  const handleClear = () => {
    onDateChange(null);
    onClose();
  };

  const renderCalendar = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const startDate = start.getDay(); // 0 is Sunday
    const daysInMonth = end.getDate();

    const days = [];
    // Empty slots for previous month
    for (let i = 0; i < startDate; i++) {
      days.push(<div key={`empty-${i}`} className="w-8 h-8"></div>);
    }

    // Days
    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);
      const isSelected = selectedDate && isSameDay(d, selectedDate);
      const isToday = isSameDay(d, new Date());

      days.push(
        <button
          key={i}
          onClick={() => handleDateSelect(d)}
          className={`w-8 h-8 text-xs rounded-md flex items-center justify-center transition-colors
            ${isSelected ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-700'}
            ${isToday && !isSelected ? 'text-blue-600 font-bold' : ''}
          `}
        >
          {i}
        </button>
      );
    }

    return days;
  };

  // Generate time slots (15 min intervals)
  const timeSlots = [];
  for (let i = 0; i < 24; i++) {
    for (let j = 0; j < 60; j += 15) {
      const hour = i.toString().padStart(2, '0');
      const minute = j.toString().padStart(2, '0');
      timeSlots.push(`${hour}:${minute}`);
    }
  }

  const content = (
    <div
      ref={popoverRef}
      style={position ? { position: 'fixed', top: position.top, left: position.left } : {}}
      className={`${position ? '' : 'absolute top-full left-0 mt-1'} bg-white rounded-xl shadow-2xl border border-gray-200 z-[9999] w-[600px] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-100 font-sans`}
    >
      {/* Header */}
      <div className="p-3 border-b border-gray-100 flex items-center justify-between bg-white">
        <div className="flex items-center gap-2 flex-1">
          <div className="w-full relative">
            <input
              type="text"
              value={inputValue}
              readOnly
              placeholder="Escribe una fecha..."
              className="w-full text-sm border-none focus:ring-0 p-0 text-gray-700 placeholder-gray-400 font-medium"
            />
            {selectedDate && (
              <button onClick={handleClear} className="absolute right-0 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500">
                <FiX size={14} />
              </button>
            )}
          </div>
        </div>
        <button
          onClick={() => setTime(time ? null : '09:00')}
          className="text-xs text-gray-500 hover:bg-gray-100 px-2 py-1 rounded transition-colors"
        >
          {time ? 'Eliminar hora' : 'Agregar hora'}
        </button>
      </div>

      <div className="flex h-[320px]">
        {/* Column 1: Shortcuts */}
        <div className="w-48 bg-gray-50 p-2 border-r border-gray-100 overflow-y-auto">
          <div className="space-y-0.5">
            {shortcuts.map((s, i) => (
              <button
                key={i}
                onClick={() => handleDateSelect(s.value)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs text-gray-600 hover:bg-gray-200 rounded text-left group transition-colors"
              >
                <span>{s.label}</span>
                <span className="text-gray-400 group-hover:text-gray-500">{s.dayLabel}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Column 2: Calendar */}
        <div className="flex-1 p-4 border-r border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-1 hover:bg-gray-100 rounded text-gray-500">
              <FiChevronLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-gray-800 capitalize">
              {format(currentMonth, 'MMMM yyyy', { locale: es })}
            </span>
            <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-1 hover:bg-gray-100 rounded text-gray-500">
              <FiChevronRight size={16} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {['do', 'lu', 'ma', 'mi', 'ju', 'vi', 'sá'].map(d => (
              <div key={d} className="text-[10px] text-center text-gray-400 font-medium uppercase tracking-wider">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {renderCalendar()}
          </div>
        </div>

        {/* Column 3: Time Picker */}
        {time && (
          <div className="w-40 overflow-y-auto border-l border-gray-100">
            {timeSlots.map((t) => (
              <button
                key={t}
                onClick={() => handleTimeSelect(t)}
                className={`w-full text-left px-4 py-2 text-xs flex items-center justify-between group hover:bg-gray-100 transition-colors
                            ${time === t ? 'bg-blue-50 text-blue-600' : 'text-gray-600'}
                        `}
              >
                <span>{t}</span>
                {time === t && <FiCornerDownLeft size={12} className="text-blue-500" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (position) {
    return createPortal(content, document.body);
  }

  return content;
};

export default DatePickerPopover;
